/**
 * Governance → StarScribe (docs/STRATEGY-starscribe.md section 11; GET/PUT /v1/admin/starscribe, GET .../status,
 * POST .../handoff): the settings with plain names, the form they are edited in, the patch a save sends (only what
 * changed), the checks made before anything is sent, and the status lines. Sizes are shown in MB and the version
 * interval in minutes; the service stores bytes and milliseconds.
 */
import { errorText } from './errors';

export type StarscribeSurface = 'desktop' | 'web' | 'office';
export type StarscribeExportFormat = 'docx' | 'pdf' | 'md';

/** The service's settings (starscribeTypes.ts starscribeSettingsSchema). */
export interface StarscribeAdminSettings {
  maxChars: number;
  maxImageBytes: number;
  maxImageBytesPerPage: number;
  maxPagesPerPerson: number;
  versionsKept: number;
  recentlyDeletedDays: number;
  maxShareRecipients: number;
  maxBlocksPerEdit: number;
  checkpointMs: number;
  inlinePageChars: number;
  readChunkChars: number;
  surfaces: StarscribeSurface[];
  sharing: boolean;
  exportFormats: StarscribeExportFormat[];
  shortcutModel: 'fast' | 'chat';
}

export type StarscribeAdminPatch = Partial<StarscribeAdminSettings>;

/** GET /v1/admin/starscribe. */
export interface StarscribeAdminView {
  settings: StarscribeAdminSettings;
  defaults: StarscribeAdminSettings;
  flag: { enabled: boolean; percent?: number };
  bridge: { notices: boolean; onedrive: boolean };
}

/** GET /v1/admin/starscribe/status: counts only. */
export interface StarscribeAdminStatus {
  storage: 'durable' | 'json';
  owners: number;
  pages: number;
  deleted: number;
  openSessions: number;
  subscribers: number;
  pendingUpdates: number;
  logBacklog: { entries: number; bytes: number };
}

export const SURFACE_OPTIONS: ReadonlyArray<{ value: StarscribeSurface; label: string }> = [
  { value: 'desktop', label: 'Desktop app' },
  { value: 'web', label: 'Gyro on the web' },
  { value: 'office', label: 'Office pane (Word and Outlook)' },
];

export const FORMAT_OPTIONS: ReadonlyArray<{ value: StarscribeExportFormat; label: string }> = [
  { value: 'docx', label: 'Word (.docx)' },
  { value: 'pdf', label: 'PDF' },
  { value: 'md', label: 'Markdown (.md)' },
];

export const SHORTCUT_OPTIONS: ReadonlyArray<{ value: StarscribeAdminSettings['shortcutModel']; label: string }> = [
  { value: 'fast', label: 'The fast model' },
  { value: 'chat', label: "The chat's model" },
];

/** The form: numbers as typed (in the unit shown), lists as chosen. */
export interface StarscribeForm {
  maxChars: string;
  maxImageMb: string;
  maxImageMbPerPage: string;
  maxPagesPerPerson: string;
  versionsKept: string;
  recentlyDeletedDays: string;
  maxShareRecipients: string;
  maxBlocksPerEdit: string;
  checkpointMinutes: string;
  inlinePageChars: string;
  readChunkChars: string;
  surfaces: StarscribeSurface[];
  sharing: boolean;
  exportFormats: StarscribeExportFormat[];
  shortcutModel: StarscribeAdminSettings['shortcutModel'];
}

type NumberKey = 'maxChars' | 'maxImageMb' | 'maxImageMbPerPage' | 'maxPagesPerPerson' | 'versionsKept' | 'recentlyDeletedDays'
  | 'maxShareRecipients' | 'maxBlocksPerEdit' | 'checkpointMinutes' | 'inlinePageChars' | 'readChunkChars';

export interface StarscribeNumberField {
  key: NumberKey;
  /** The plain name on screen (never the setting's identifier). */
  label: string;
  hint: string;
  /** In the unit shown. */
  bounds: { min: number; max: number };
  /** A whole number only (every field but the two MB sizes). */
  integer: boolean;
}

const MB = 1024 * 1024;

/** The limits, in the order the card shows them. */
export const STARSCRIBE_NUMBER_FIELDS: readonly StarscribeNumberField[] = [
  { key: 'maxChars', label: 'Characters on one page', bounds: { min: 1_000, max: 2_000_000 }, integer: true, hint: 'Default 200,000 (about 80 to 100 printed pages).' },
  { key: 'maxImageMb', label: 'One picture (MB)', bounds: { min: 0.1, max: 50 }, integer: false, hint: 'Default 10.' },
  { key: 'maxImageMbPerPage', label: 'All pictures on one page (MB)', bounds: { min: 0.1, max: 500 }, integer: false, hint: 'Default 50.' },
  { key: 'maxPagesPerPerson', label: 'Pages per person', bounds: { min: 1, max: 100_000 }, integer: true, hint: 'Default 1,000, Recently deleted included.' },
  { key: 'versionsKept', label: 'Versions kept per page', bounds: { min: 5, max: 500 }, integer: true, hint: 'Default 50; the oldest is dropped first.' },
  { key: 'recentlyDeletedDays', label: 'Days in Recently deleted', bounds: { min: 1, max: 365 }, integer: true, hint: 'Default 30; after that a deleted page is erased.' },
  { key: 'maxShareRecipients', label: 'People one page can be shared with', bounds: { min: 1, max: 500 }, integer: true, hint: 'Default 50.' },
  { key: 'maxBlocksPerEdit', label: 'Blocks one Gyro edit may change', bounds: { min: 10, max: 5_000 }, integer: true, hint: 'Default 200; a larger rewrite runs in steps.' },
  { key: 'checkpointMinutes', label: 'Minutes between versions while typing', bounds: { min: 1, max: 60 }, integer: true, hint: 'Default 5.' },
  { key: 'inlinePageChars', label: 'Page text sent with each question (characters)', bounds: { min: 0, max: 100_000 }, integer: true, hint: 'Default 12,000; a longer page is read in parts.' },
  { key: 'readChunkChars', label: 'Characters in one read part', bounds: { min: 5_000, max: 100_000 }, integer: true, hint: 'Default 40,000.' },
];

const trimNumber = (value: number): string => String(Math.round(value * 100) / 100);

export function starscribeForm(settings: StarscribeAdminSettings): StarscribeForm {
  return {
    maxChars: String(settings.maxChars),
    maxImageMb: trimNumber(settings.maxImageBytes / MB),
    maxImageMbPerPage: trimNumber(settings.maxImageBytesPerPage / MB),
    maxPagesPerPerson: String(settings.maxPagesPerPerson),
    versionsKept: String(settings.versionsKept),
    recentlyDeletedDays: String(settings.recentlyDeletedDays),
    maxShareRecipients: String(settings.maxShareRecipients),
    maxBlocksPerEdit: String(settings.maxBlocksPerEdit),
    checkpointMinutes: trimNumber(settings.checkpointMs / 60_000),
    inlinePageChars: String(settings.inlinePageChars),
    readChunkChars: String(settings.readChunkChars),
    surfaces: [...settings.surfaces],
    sharing: settings.sharing,
    exportFormats: [...settings.exportFormats],
    shortcutModel: settings.shortcutModel,
  };
}

const numberOf = (text: string): number => Number(text.trim().replace(/,/g, ''));

/** The first problem with the form, naming the field, or null. Nothing is sent while there is one. */
export function validateStarscribe(form: StarscribeForm): string | null {
  for (const field of STARSCRIBE_NUMBER_FIELDS) {
    const text = form[field.key].trim();
    const value = numberOf(text);
    if (!text || !Number.isFinite(value)) return `${field.label}: enter a number.`;
    if (field.integer && !Number.isInteger(value)) return `${field.label}: enter a whole number.`;
    if (value < field.bounds.min || value > field.bounds.max) {
      return `${field.label}: enter ${field.bounds.min.toLocaleString('en-US')} to ${field.bounds.max.toLocaleString('en-US')}.`;
    }
  }
  if (numberOf(form.maxImageMb) > numberOf(form.maxImageMbPerPage)) return 'One picture (MB): it cannot be larger than all pictures on one page.';
  return null;
}

/** Only what changed, in the service's units. Call after validateStarscribe. */
export function starscribePatch(form: StarscribeForm, stored: StarscribeAdminSettings): StarscribeAdminPatch {
  const next: StarscribeAdminSettings = {
    maxChars: numberOf(form.maxChars),
    maxImageBytes: Math.round(numberOf(form.maxImageMb) * MB),
    maxImageBytesPerPage: Math.round(numberOf(form.maxImageMbPerPage) * MB),
    maxPagesPerPerson: numberOf(form.maxPagesPerPerson),
    versionsKept: numberOf(form.versionsKept),
    recentlyDeletedDays: numberOf(form.recentlyDeletedDays),
    maxShareRecipients: numberOf(form.maxShareRecipients),
    maxBlocksPerEdit: numberOf(form.maxBlocksPerEdit),
    checkpointMs: Math.round(numberOf(form.checkpointMinutes) * 60_000),
    inlinePageChars: numberOf(form.inlinePageChars),
    readChunkChars: numberOf(form.readChunkChars),
    surfaces: SURFACE_OPTIONS.map((option) => option.value).filter((value) => form.surfaces.includes(value)),
    sharing: form.sharing,
    exportFormats: FORMAT_OPTIONS.map((option) => option.value).filter((value) => form.exportFormats.includes(value)),
    shortcutModel: form.shortcutModel,
  };
  // A size shown in MB that rounds to the stored bytes is unchanged (the form shows two decimals).
  if (trimNumber(stored.maxImageBytes / MB) === form.maxImageMb.trim()) next.maxImageBytes = stored.maxImageBytes;
  if (trimNumber(stored.maxImageBytesPerPage / MB) === form.maxImageMbPerPage.trim()) next.maxImageBytesPerPage = stored.maxImageBytesPerPage;
  const patch: StarscribeAdminPatch = {};
  for (const key of Object.keys(next) as Array<keyof StarscribeAdminSettings>) {
    if (JSON.stringify(next[key]) !== JSON.stringify(stored[key])) (patch as Record<string, unknown>)[key] = next[key];
  }
  return patch;
}

/** The status panel's title: whether StarScribe is on, for how many people. */
export function starscribeSwitchText(view: StarscribeAdminView): string {
  if (!view.flag.enabled) return 'StarScribe is off. Nobody sees it until the starscribe flag is turned on under Feature flags.';
  const percent = view.flag.percent ?? 100;
  return percent >= 100 ? 'StarScribe is on for everyone.' : `StarScribe is on for ${percent}% of people.`;
}

/** The status panel's counts. */
export function starscribeStatusText(status: StarscribeAdminStatus): string {
  const n = (value: number) => value.toLocaleString('en-US');
  return `${n(status.pages)} page(s) owned by ${n(status.owners)} people, ${n(status.deleted)} in Recently deleted; `
    + `${n(status.openSessions)} open now with ${n(status.subscribers)} viewer(s).`;
}

/** The Bridge-dependent parts, while their routes are not connected. */
export function starscribeBridgeLines(view: StarscribeAdminView): string[] {
  const lines: string[] = [];
  if (!view.bridge.notices) lines.push('Notices (shares, mentions) wait for The Bridge: not connected.');
  if (!view.bridge.onedrive) lines.push('Save to OneDrive waits for The Bridge: not connected.');
  return lines;
}

export function starscribeReadFailure(error: unknown): string {
  return errorText(error, 'The StarScribe settings could not be read. Nothing is shown as a default.');
}
